import React, { useCallback } from 'react';

const CustomerDisplayCard = ({
    selectedCustomer,
    loading = false
}) => {
    // Format customer display based on type
    const getCustomerLabel = useCallback((customer) => {
        if (!customer) return '';

        if (customer.type === 'company') {
            const orgNumber = customer.organization_number || customer.orgnumber;
            return `${customer.name}${orgNumber ? ` (${orgNumber})` : ''}`;
        } else {
            const firstName = customer.first_name || customer.firstName || '';
            const lastName = customer.last_name || customer.lastName || '';
            const fullName = customer.name || `${firstName} ${lastName}`.trim();
            return fullName;
        }
    }, []);

    // Get customer address for display
    const getCustomerAddress = useCallback((customer) => {
        if (!customer?.addresses?.length) return null;
        const address = customer.addresses.find(a => a.address_type === 'billing') || customer.addresses[0];
        return address;
    }, []);

    return (
        <>
            <style>{`
                .customer-display-card {
                    background: white;
                    border-radius: 12px;
                    padding: 2rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e5e7eb;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                
                .section-icon {
                    width: 40px;
                    height: 40px;
                    background: #f0f9ff;
                    border: 1px solid #0284c7;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #0284c7;
                    font-size: 1.25rem;
                }
                
                .section-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }
                
                .section-subtitle {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin: 0.25rem 0 0 0;
                }
                
                .readonly-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.375rem;
                    padding: 0.375rem 0.75rem;
                    background: #f3f4f6;
                    color: #6b7280;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    margin-left: 1rem;
                }
                
                .selected-customer-section {
                    margin-top: 0;
                    padding-top: 0;
                    border-top: none;
                }
                
                .customer-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                }
                
                .customer-info {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                }
                
                .customer-avatar {
                    width: 48px;
                    height: 48px;
                    border-radius: 8px;
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6c757d;
                    font-size: 1.25rem;
                    flex-shrink: 0;
                }
                
                .customer-details h3 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 0.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .success-icon {
                    width: 16px;
                    height: 16px;
                    background: #16a34a;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 0.625rem;
                }
                
                .customer-type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.75rem;
                    background: #f8f9fa;
                    color: #6c757d;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .company-info {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    padding-bottom: 1.5rem;
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }
                
                .info-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }
                
                .info-value {
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #1e293b;
                }
                
                .contact-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1.5rem;
                }
                
                .contact-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                }
                
                .contact-icon {
                    width: 32px;
                    height: 32px;
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6c757d;
                    font-size: 0.875rem;
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }
                
                .contact-details {
                    min-width: 0;
                    flex: 1;
                }
                
                .contact-details .info-label {
                    margin-bottom: 0.25rem;
                }
                
                .contact-details .info-value {
                    color: #3b82f6;
                    text-decoration: none;
                    word-break: break-word;
                }
                
                .contact-details .info-value:hover {
                    color: #1d4ed8;
                    text-decoration: underline;
                }
                
                .address-text {
                    line-height: 1.4;
                }
                
                .customer-footer {
                    margin-top: 1.5rem;
                    padding-top: 1rem;
                    border-top: 1px solid #f1f5f9;
                }
                
                .customer-number {
                    font-size: 0.75rem;
                    color: #6b7280;
                    font-weight: 500;
                }
                
                .customer-number strong {
                    color: #1e293b;
                    font-weight: 600;
                }
                
                @media (max-width: 768px) {
                    .customer-display-card {
                        padding: 1.5rem;
                    }
                    
                    .customer-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: stretch;
                    }
                    
                    .contact-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .company-info {
                        grid-template-columns: 1fr;
                    }
                    
                    .section-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }
                }
            `}</style>

            <div className="customer-display-card">
                {/* Section Header - Same as other sections */}
                <div className="section-header">
                    <div className="section-icon">
                        <i className="bi bi-person"></i>
                    </div>
                    <div>
                        <h2 className="section-title">
                            Kund
                            <span className="readonly-badge">
                                <i className="bi bi-lock-fill"></i>
                                Från originalfaktura
                            </span>
                        </h2>
                        <p className="section-subtitle">
                            Kunduppgifter från den ursprungliga fakturan
                        </p>
                    </div>
                </div>

                {/* Customer Display */}
                {selectedCustomer && (
                    <div className="selected-customer-section">
                        {/* Header */}
                        <div className="customer-header">
                            <div className="customer-info">
                                <div className="customer-avatar">
                                    <i className={`bi bi-${selectedCustomer.type === 'company' ? 'building' : 'person-fill'}`}></i>
                                </div>
                                <div className="customer-details">
                                    <h3>
                                        {selectedCustomer.name}
                                        <div className="success-icon">
                                            <i className="bi bi-check"></i>
                                        </div>
                                    </h3>
                                    <span className="customer-type-badge">
                                        <i className={`bi bi-${selectedCustomer.type === 'company' ? 'building' : 'person'}`}></i>
                                        {selectedCustomer.type === 'company' ? 'Företag' : 'Privatperson'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Company Info */}
                        {selectedCustomer.type === 'company' && selectedCustomer.orgnumber && (
                            <div className="company-info">
                                <div className="info-item">
                                    <div className="info-label">Organisationsnummer</div>
                                    <div className="info-value">{selectedCustomer.orgnumber}</div>
                                </div>
                                {selectedCustomer.vatnumber && (
                                    <div className="info-item">
                                        <div className="info-label">VAT-nummer</div>
                                        <div className="info-value">{selectedCustomer.vatnumber}</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Contact Information */}
                        <div className="contact-grid">
                            {selectedCustomer.email && (
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <i className="bi bi-envelope"></i>
                                    </div>
                                    <div className="contact-details">
                                        <div className="info-label">E-post</div>
                                        <a
                                            href={`mailto:${selectedCustomer.email}`}
                                            className="info-value"
                                        >
                                            {selectedCustomer.email}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {selectedCustomer.phone && (
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <i className="bi bi-telephone"></i>
                                    </div>
                                    <div className="contact-details">
                                        <div className="info-label">Telefon</div>
                                        <a
                                            href={`tel:${selectedCustomer.phone}`}
                                            className="info-value"
                                        >
                                            {selectedCustomer.phone}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {getCustomerAddress(selectedCustomer) && (
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <i className="bi bi-geo-alt"></i>
                                    </div>
                                    <div className="contact-details">
                                        <div className="info-label">Faktureringsadress</div>
                                        <div className="info-value address-text">
                                            {(() => {
                                                const addr = getCustomerAddress(selectedCustomer);
                                                return (
                                                    <>
                                                        {addr.street1 && <div>{addr.street1}</div>}
                                                        {addr.street2 && <div>{addr.street2}</div>}
                                                        <div>
                                                            {addr.postal_code && `${addr.postal_code} `}
                                                            {addr.city}
                                                        </div>
                                                        {addr.country && addr.country !== 'Sverige' && (
                                                            <div>{addr.country}</div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {(selectedCustomer.customer_number || selectedCustomer.number) && (
                            <div className="customer-footer">
                                <div className="customer-number">
                                    Kundnummer: <strong>{selectedCustomer.customer_number || selectedCustomer.number}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default CustomerDisplayCard;
